import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { selectAdminToken } from '../sessionSlice';
import { fetchThemeData } from '../themeSlice';
import { apiUrl } from '../../utils/apiUrl';
import { GAME_CATALOG, getActiveGame, getGameDisplayName } from '../../utils/gameHelpers';
import './adminGameBar.css';

export default function AdminGameBar() {
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data: themeData } = useSelector((s) => s.theme);

  const [catalog, setCatalog] = useState(GAME_CATALOG);
  const [activeGame, setActiveGame] = useState('cheers_to_peers');
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (themeData) {
      setActiveGame(getActiveGame(themeData));
    }
  }, [themeData]);

  const loadCatalog = useCallback(async () => {
    if (!adminToken) return;
    try {
      const resp = await fetch(apiUrl('/getGameCatalog'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ currentTheme: currentTheme || themeData?.themename }),
      });
      const json = await resp.json();
      if (json.success) {
        if (Array.isArray(json.catalog)) setCatalog(json.catalog);
        if (json.activeGame) setActiveGame(json.activeGame);
      }
    } catch (e) {
      console.error('getGameCatalog', e);
    }
  }, [adminToken, currentTheme, themeData?.themename]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const activateGame = async (gameId) => {
    if (gameId === activeGame || saving) return;

    const gameName = catalog.find((g) => g.id === gameId)?.name || gameId;
    const confirm = await Swal.fire({
      title: `Activate ${gameName}?`,
      text: 'Admin pages will show this game\'s categories, rules, and content. Other games keep their saved settings.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Activate',
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    setPending(gameId);
    try {
      const resp = await fetch(apiUrl('/setActiveGame'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          activeGame: gameId,
          currentTheme: currentTheme || themeData?.themename || 'theme0',
        }),
      });
      const json = await resp.json();
      if (!resp.ok || !json.success) {
        Swal.fire('Error', json.error || 'Could not switch game.', 'error');
        return;
      }
      setActiveGame(gameId);
      await dispatch(fetchThemeData({ themeId: currentTheme || themeData?.themename, isAdmin: true })).unwrap();
      Swal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: `${gameName} is now active`,
        showConfirmButton: false,
        timer: 2000,
      });
    } catch (e) {
      Swal.fire('Error', e.message || 'Switch failed.', 'error');
    } finally {
      setSaving(false);
      setPending(null);
    }
  };

  const displayName = getGameDisplayName(activeGame);

  return (
    <div className="admin-game-bar">
      <div className="admin-game-bar-top">
        <span className="admin-game-bar-count">{catalog.length} games</span>
        <span className="admin-game-bar-editing">
          Editing: <strong>{displayName}</strong>
        </span>
      </div>
      <div className="admin-game-bar-scroll">
        {catalog.map((game) => (
          <button
            key={game.id}
            type="button"
            className={`admin-game-bar-card${activeGame === game.id ? ' is-active' : ''}${pending === game.id ? ' is-pending' : ''}`}
            onClick={() => activateGame(game.id)}
            disabled={saving}
            title={game.description}
          >
            <span className="admin-game-bar-card-name">{game.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
